import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { prospectActions } from '@nxsys/store/slices/prospect/reducer/prospectSlice';
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AgencyForm, { AgencyFormRef, AgencyFormValues } from './AgencyForm';

type CreateAgencyProps = {
  isOpen: boolean;
  toggle: () => void;
};

const CreateAgencyModal: React.FC<CreateAgencyProps> = ({
  isOpen,
  toggle,
}: CreateAgencyProps) => {
  const { messages } = useTypedIntl();

  const dispatch = useDispatch<AppDispatch>();
  const {
    agencyList: { queryParams },
  } = useSelector((state: AppState) => state.prospectStore);

  const formRef = useRef<AgencyFormRef>(null);

  const [isCreating, setIsCreating] = useState(false);

  const handleSubmit = async (values: AgencyFormValues) => {
    setIsCreating(true);
    try {
      const params = {
        email: values.email ?? null,
        phone: values.phone ?? null,
        name: values.name ?? null,
        contactName: values.contactName ?? null,
      };
      // unwrap() so a rejected request throws — without it the thunk resolves
      // and the modal closes as if the create had succeeded
      await dispatch(prospectActions.createAgencyProspect(params)).unwrap();
      await dispatch(prospectActions.getAgencyProspectList({ ...queryParams }));
      toggle();
      formRef.current?.reset();
    } catch (error) {
      console.error(error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <AppModalContainer
      title={messages['prospect.addNewAgency']}
      isOpen={isOpen}
      toggle={toggle}
      // Unmount the form on close so nothing typed before carries over
      destroyOnClose
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => formRef.current?.submit(),
          disabled: isCreating,
          loading: isCreating,
        },
      ]}
    >
      <AgencyForm formRef={formRef} onSubmit={handleSubmit} />
    </AppModalContainer>
  );
};

export default CreateAgencyModal;
